import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useOrgWidgetData from '../hooks/useOrgWidgetData';
import OrgSiteRow from './OrgSiteRow';
import OrgWidgetFrame from './OrgWidgetFrame';
import {
  ColumnHeaderRow,
  Content,
  EmptyText,
  HeaderBarLabel,
  HeaderName,
  HeaderValueLabel,
  Legend,
  LegendItem,
  LegendSwatch,
  Meta,
  OrgSummary,
  ScrollArea,
  SecondaryValue,
  SegBar,
  Segment,
  SiteList,
  SiteValue,
  SummaryHeader,
  SummaryLabel,
  SummaryValue,
  ZeroToggle
} from './OrgWidgetLayout';
import { OrgWidgetDataState } from './OrgWidgetStates';
import { getOrgWidgetTitle, useResolveOrgWidgetMeta } from './orgWidgetMeta';

const defaultFormatValue = value => (value != null ? value : 0);

const sumSegments = (segments, segmentKeys) =>
  segmentKeys.reduce((sum, key) => sum + ((segments && segments[key]) || 0), 0);

export const buildSegmentedBar = (segments, segmentKeys, segmentColors, total, compact, barSize, inRow) => (
  <SegBar compact={compact} medium={barSize === 'medium'} inRow={inRow}>
    {segmentKeys.map((key) => {
      const count = (segments && segments[key]) || 0;
      if (!count) return null;
      return (
        <Segment
          key={key}
          pct={(count / total) * 100}
          color={segmentColors[key]}
        />
      );
    })}
  </SegBar>
);

const OrgSegmentedBySiteWidget = ({
  organisationId,
  widgetsMetadata,
  title,
  widgetName,
  emptyMessageKey,
  emptyMessageDefault,
  noSitesMessageKey,
  noSitesMessageDefault,
  summaryLabelKey,
  summaryLabelDefault,
  segmentKeys,
  getSegmentKeys,
  segmentColors,
  segmentLabelKeys,
  getSummaryValue,
  formatSummaryValue,
  renderSummaryMeta,
  getSiteRowPrimary,
  getSiteRowSecondary,
  shouldShowSiteSecondary,
  columnHeaders,
  getSiteTooltip,
  getSummaryTooltip,
  getValueTone,
  siteLinkTo,
  compact,
  loadingHeight,
  requireSites,
  barSize,
  isEmptyData
}) => {
  const { t } = useTranslation();
  const [showZeros, setShowZeros] = useState(false);
  const meta = useResolveOrgWidgetMeta(widgetsMetadata, widgetName);
  const widgetTitle = getOrgWidgetTitle(title, meta);
  const { data, loading, error, reload } = useOrgWidgetData(organisationId, meta);

  const resolvedSegmentKeys = getSegmentKeys
    ? getSegmentKeys(data)
    : segmentKeys;
  const orgSummary = data && data.org_summary;
  const sites = (data && data.sites) || [];
  const formatSummary = formatSummaryValue || defaultFormatValue;
  const orgSegmentTotal = orgSummary
    ? sumSegments(orgSummary.segments, resolvedSegmentKeys) || 1
    : 1;

  // Only show legend swatches for conditions that actually occur in the org, so
  // absent severities (often Slight/Moderate/Unknown) don't wrap the legend onto
  // a second row. Bars already skip zero-count segments, so the two stay in sync.
  const legendKeys = orgSummary && orgSummary.segments
    ? resolvedSegmentKeys.filter(key => (orgSummary.segments[key] || 0) > 0)
    : resolvedSegmentKeys;

  // Collapse the sites that have nothing (all segments zero) into one muted,
  // toggleable row — but only when some sites DO have data, so a widget where
  // every site is zero still lists them rather than hiding everything.
  const zeroCount = sites.filter(
    site => sumSegments(site.segments || {}, resolvedSegmentKeys) === 0
  ).length;
  const canCollapseZeros = zeroCount > 0 && zeroCount < sites.length;
  const visibleSites = (canCollapseZeros && !showZeros)
    ? sites.filter(site => sumSegments(site.segments || {}, resolvedSegmentKeys) > 0)
    : sites;

  const empty = isEmptyData
    ? isEmptyData(error, data, sites)
    : (error || !data || (requireSites && !sites.length));

  const renderEmptySites = () => {
    if (!noSitesMessageKey) return null;
    return (
      <EmptyText compact={compact}>
        {t(noSitesMessageKey, noSitesMessageDefault)}
      </EmptyText>
    );
  };

  return (
    <OrgWidgetDataState
      title={widgetTitle}
      loading={loading}
      hasError={error}
      onRetry={reload}
      isEmpty={empty}
      loadingHeight={loadingHeight}
      compact={compact}
      emptyMessage={t(emptyMessageKey, emptyMessageDefault)}
    >
      <OrgWidgetFrame title={widgetTitle}>
        <ScrollArea>
          <Content compact={compact}>
            {orgSummary && (
              <OrgSummary
                compact={compact}
                title={getSummaryTooltip ? getSummaryTooltip(orgSummary, t) : undefined}
              >
                <SummaryHeader compact={compact}>
                  <SummaryLabel compact={compact}>
                    {t(summaryLabelKey, summaryLabelDefault)}
                  </SummaryLabel>
                  <SummaryValue compact={compact}>
                    {formatSummary(getSummaryValue(orgSummary))}
                  </SummaryValue>
                </SummaryHeader>
                {buildSegmentedBar(
                  orgSummary.segments,
                  resolvedSegmentKeys,
                  segmentColors,
                  orgSegmentTotal,
                  compact,
                  barSize
                )}
                {renderSummaryMeta ? (
                  <Meta compact={compact}>{renderSummaryMeta(t, orgSummary)}</Meta>
                ) : null}
              </OrgSummary>
            )}

            {legendKeys.length > 0 && (
              <Legend compact={compact}>
                {legendKeys.map(key => (
                  <LegendItem key={key} compact={compact}>
                    <LegendSwatch color={segmentColors[key]} compact={compact} />
                    {t(segmentLabelKeys[key], key)}
                  </LegendItem>
                ))}
              </Legend>
            )}

            {columnHeaders && sites.length ? (
              <ColumnHeaderRow compact={compact}>
                <HeaderName />
                <HeaderBarLabel compact={compact}>{columnHeaders.bar}</HeaderBarLabel>
                <HeaderValueLabel compact={compact}>{columnHeaders.value}</HeaderValueLabel>
              </ColumnHeaderRow>
            ) : null}

            <SiteList compact={compact}>
              {sites.length ? visibleSites.map((site) => {
                const segments = site.segments || {};
                const total = sumSegments(segments, resolvedSegmentKeys) || 1;
                const showSecondary = shouldShowSiteSecondary
                  ? shouldShowSiteSecondary(orgSummary, site, total)
                  : false;
                // Hovering anywhere in the row (name, bar, or value) shows the
                // fully-labelled breakdown, so the terse per-site number is never
                // ambiguous. Native title — robust inside the scrolling list.
                const tooltip = getSiteTooltip ? getSiteTooltip(site, orgSummary, t) : undefined;

                return (
                  <OrgSiteRow
                    key={site.account_id || site.name}
                    accountId={site.account_id}
                    name={site.name}
                    tooltip={tooltip}
                    compact={compact}
                    to={siteLinkTo}
                  >
                    {buildSegmentedBar(
                      segments,
                      resolvedSegmentKeys,
                      segmentColors,
                      total,
                      compact,
                      barSize,
                      true
                    )}
                    <SiteValue compact={compact} tone={getValueTone ? getValueTone(site, orgSummary) : undefined}>
                      {getSiteRowPrimary(site, orgSummary)}
                      {showSecondary && (
                        <SecondaryValue>
                          {getSiteRowSecondary(site, orgSummary, total)}
                        </SecondaryValue>
                      )}
                    </SiteValue>
                  </OrgSiteRow>
                );
              }) : renderEmptySites()}
              {canCollapseZeros && (
                <ZeroToggle
                  compact={compact}
                  type="button"
                  onClick={() => setShowZeros(prev => !prev)}
                >
                  {showZeros
                    ? t('orgView.showLess', 'Show less')
                    : t('orgView.sitesWithNone', { count: zeroCount, defaultValue: '+{{count}} sites with none' })}
                </ZeroToggle>
              )}
            </SiteList>
          </Content>
        </ScrollArea>
      </OrgWidgetFrame>
    </OrgWidgetDataState>
  );
};

OrgSegmentedBySiteWidget.propTypes = {
  organisationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  widgetsMetadata: PropTypes.object,
  title: PropTypes.string,
  widgetName: PropTypes.string.isRequired,
  emptyMessageKey: PropTypes.string.isRequired,
  emptyMessageDefault: PropTypes.string.isRequired,
  noSitesMessageKey: PropTypes.string,
  noSitesMessageDefault: PropTypes.string,
  summaryLabelKey: PropTypes.string.isRequired,
  summaryLabelDefault: PropTypes.string.isRequired,
  segmentKeys: PropTypes.arrayOf(PropTypes.string),
  getSegmentKeys: PropTypes.func,
  segmentColors: PropTypes.object.isRequired,
  segmentLabelKeys: PropTypes.object.isRequired,
  getSummaryValue: PropTypes.func.isRequired,
  formatSummaryValue: PropTypes.func,
  renderSummaryMeta: PropTypes.func,
  getSiteRowPrimary: PropTypes.func.isRequired,
  getSiteRowSecondary: PropTypes.func,
  shouldShowSiteSecondary: PropTypes.func,
  columnHeaders: PropTypes.shape({ bar: PropTypes.node, value: PropTypes.node }),
  getSiteTooltip: PropTypes.func,
  getSummaryTooltip: PropTypes.func,
  getValueTone: PropTypes.func,
  // A path, or a builder run with the site's account id that may seed the
  // destination page's filters — see OrgSiteRow.
  siteLinkTo: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  compact: PropTypes.bool,
  loadingHeight: PropTypes.string,
  requireSites: PropTypes.bool,
  barSize: PropTypes.oneOf(['default', 'medium']),
  isEmptyData: PropTypes.func
};

OrgSegmentedBySiteWidget.defaultProps = {
  title: null,
  widgetsMetadata: null,
  noSitesMessageKey: null,
  noSitesMessageDefault: null,
  segmentKeys: null,
  getSegmentKeys: null,
  formatSummaryValue: null,
  renderSummaryMeta: null,
  isEmptyData: null,
  compact: false,
  loadingHeight: '120px',
  requireSites: true,
  barSize: 'default',
  getSiteRowSecondary: () => null,
  shouldShowSiteSecondary: () => false,
  columnHeaders: null,
  getSiteTooltip: null,
  getSummaryTooltip: null,
  getValueTone: null,
  siteLinkTo: null
};

export default OrgSegmentedBySiteWidget;
